/**
 * File: apps/qbench-cp/reactapp/src/shared/hooks/misc/useSearchableSampleFields.js
 *
 * Purpose: Resolves the sample-level fields the CPv2 order search actually matches on, so the
 *     search bar can name them truthfully instead of guessing.
 *
 * Scope: CPv2 order search (the Orders search bar placeholder); the client-side mirror of
 *     SampleFieldConfigurationService.resolve_searchable_sample_fields.
 *
 * Tags: customer-portal, field-config
 *
 * Overview: The backend resolves the searchable "top 3" per portal customer — a field screen when
 *     the Field Screen system is active, else the customer's sole non-global division, else Global
 *     (see _resolve_cpv2_request_division_id in blueprints/customer_portal_v2_orders.py). This hook
 *     reproduces that decision from the /v1/fields payload: it probes the customer's sole
 *     non-global division and falls back to Global when that division carries no config, which is
 *     also how a field-screen customer resolves (the endpoint then returns only the Global id,
 *     carrying the customer's screen config). It then applies the same field filters as
 *     get_display_fields(for_search=True, portal=True, ignore_fields=['report']) and takes the same
 *     first three. The payload arrives already sorted by portal_order with portal-hidden fields
 *     stripped, so ordering needs no further work here.
 *
 * Dependencies: context/fields (getFields); the field ordering/visibility contract of
 *     unit_tested_utils/blueprint_utils/customer_portal_v2_utils.transform_field_config.
 *
 * Exports: default useSearchableSampleFields hook; SEARCHABLE_SAMPLE_FIELD_LIMIT.
 */

import { useFieldConfig } from 'context/fields';
import React from 'react';
import { useAppStore } from 'App/store';
import { EntityType } from 'shared/constants/entityTypes';
import { AddlFieldType, ENABLED_MODULES } from 'shared/constants/fields';
import usePermissionCheck from './usePermissionCheck';

// Mirrors the `limit=3` default on get_display_fields that resolve_searchable_sample_fields relies on.
export const SEARCHABLE_SAMPLE_FIELD_LIMIT = 3;

// get_display_fields drops these by name for a search resolution: 'report' via the caller's
// ignore_fields, 'attachments' via the for_search branch.
const IGNORED_FIELD_NAMES = ['report', 'attachments'];

// Field types get_display_fields skips for an additional-attribute field when for_search is set.
const IGNORED_SEARCH_FIELD_TYPES = [
  AddlFieldType.HYPERLINK,
  AddlFieldType.OBJECT_RELATION,
  AddlFieldType.OBJECT_RELATION_MULTI,
  AddlFieldType.RICH_TEXT,
];

function isSearchable(field) {
  if (IGNORED_FIELD_NAMES.includes(field.name)) return false;
  // The limit=3 branch of get_display_fields adds RICH_TEXT to ignore_field_types outright.
  if (field.fieldType === AddlFieldType.RICH_TEXT) return false;
  if (field.additionalAttribute) {
    return !IGNORED_SEARCH_FIELD_TYPES.includes(field.fieldType);
  }
  return true;
}

/**
 * The sample fields the order search matches on, in the order the backend resolves them.
 * @returns {Array<Object>} Up to SEARCHABLE_SAMPLE_FIELD_LIMIT field configs.
 */
export default function useSearchableSampleFields() {
  const { getFields } = useFieldConfig();
  const {
    appState: { customer, globalDivisionId },
  } = useAppStore();
  const divisionsEnabled = usePermissionCheck(ENABLED_MODULES.DIVISIONS);

  // Only a customer tied to exactly one non-global division resolves to that division; zero or
  // several fall back to Global, as does every customer when divisions are off.
  const soleDivisionId = React.useMemo(() => {
    if (!divisionsEnabled) return null;
    const nonGlobalIds = (customer?.divisions ?? [])
      .map((division) => division.id)
      .filter((id) => id != null && id !== globalDivisionId);
    return nonGlobalIds.length === 1 ? nonGlobalIds[0] : null;
  }, [customer, divisionsEnabled, globalDivisionId]);

  return React.useMemo(() => {
    const options = { entityType: EntityType.SAMPLE, showAllFields: true };
    // showAllFields mirrors the backend: resolve_searchable_sample_fields passes no
    // for_order_request, so the order_request_field filter getFields applies by default is wrong here.
    const divisionFields =
      soleDivisionId == null
        ? []
        : getFields({ ...options, divisionId: soleDivisionId });
    const resolved = divisionFields.length
      ? divisionFields
      : getFields({ ...options, useGlobal: true });

    return resolved
      .filter(isSearchable)
      .slice(0, SEARCHABLE_SAMPLE_FIELD_LIMIT);
  }, [getFields, soleDivisionId]);
}
